/**
 * Serviço de tema.
 *
 * Um lugar só escreve `data-theme` no `<html>` e guarda a escolha — nenhuma tela mexe em
 * cor por conta própria. Escuro é o padrão do `:root`, então ele **não** tem atributo:
 * quem nunca escolheu vê exatamente o app de antes.
 *
 * `system` não é um tema, é uma regra: resolve por `prefers-color-scheme` e reage quando
 * o aparelho vira o dia. Por isso a escolha guardada e o tema aplicado são coisas
 * diferentes, e existe `resolve` no meio.
 */

const KEY = 'sentido:theme';

/** As opções que a tela de escolha oferece, nesta ordem. */
export const THEMES = ['dark', 'light', 'contrast', 'system'];

/** O tema que o aparelho pede quando a escolha é `system`. */
function systemTheme() {
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

/** O tema que vai para a tela, já resolvido: nunca devolve `system`. */
export function resolve(choice) {
  if (choice === 'system') return systemTheme();
  return THEMES.includes(choice) ? choice : 'dark';
}

/** A escolha guardada, ou `null` quando o jogador ainda não escolheu. */
export function readChoice() {
  try {
    const value = localStorage.getItem(KEY);
    return THEMES.includes(value) ? value : null;
  } catch {
    // Navegador em modo privado: o app funciona, só não lembra.
    return null;
  }
}

/**
 * Pinta a tela sem gravar nada — é o que a pré-visualização da primeira abertura usa.
 * Devolve o tema efetivo.
 */
export function apply(choice) {
  const theme = resolve(choice);
  const root = document.documentElement;
  if (theme === 'dark') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);

  // A barra do navegador e a do PWA seguem o chão da tela.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', { light: '#F2EFF9', contrast: '#000000' }[theme] || '#09070E');
  return theme;
}

/** Grava e pinta. É o único caminho que persiste. */
export function set(choice) {
  try {
    localStorage.setItem(KEY, choice);
  } catch {
    // Sem armazenamento a escolha vale para esta sessão, e é melhor que recusar a troca.
  }
  return apply(choice);
}

/**
 * Chamado no `main.js` **antes** de montar o app: pintar depois do primeiro quadro faz a
 * tela piscar no tema errado. Também assina a mudança do sistema, que só importa para
 * quem escolheu `system`.
 */
export function start() {
  const choice = readChoice() || 'dark';
  apply(choice);

  const query = window.matchMedia?.('(prefers-color-scheme: light)');
  if (query) {
    const onChange = () => { if (readChoice() === 'system') apply('system'); };
    if (query.addEventListener) query.addEventListener('change', onChange);
    else query.addListener(onChange);
  }
  return choice;
}

/** `true` enquanto o jogador não escolheu — é o que abre a tela de escolha. */
export function firstTime() {
  return readChoice() === null;
}

/** O aparelho pede menos movimento? */
export function systemReducedMotion() {
  return !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}
